
import React, { useState } from 'react';
import { Calendar, Users, Ship, ShieldCheck, Loader2, CheckCircle2, ArrowRight, Mail, Compass, MapPin, Plus, Trash2, Globe, User } from 'lucide-react';
import { dbService } from '../services/dbService';
import { SiteContent, Tourist } from '../types';

interface RegistrationProps {
  data: SiteContent['registration'];
  isAdmin: boolean;
  onUpdate: (data: SiteContent['registration']) => void;
}

const Registration: React.FC<RegistrationProps> = ({ data, isAdmin, onUpdate }) => {
  const [formData, setFormData] = useState({
    email: '',
    arrival_date: '',
    departure_date: '',
    purpose: 'Leisure',
    city: '',
    country: '',
    tourists: [{ name: '', age: '', gender: 'Prefer not to say' }] as Tourist[]
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');

  const handleTouristChange = (index: number, field: keyof Tourist, value: string) => {
    const newTourists = [...formData.tourists];
    newTourists[index] = { ...newTourists[index], [field]: value };
    setFormData({ ...formData, tourists: newTourists });
  };

  const addTourist = () => {
    setFormData({ 
      ...formData, 
      tourists: [...formData.tourists, { name: '', age: '', gender: 'Prefer not to say' }] 
    });
  };

  const removeTourist = (index: number) => {
    if (formData.tourists.length === 1) return;
    const newTourists = formData.tourists.filter((_, i) => i !== index);
    setFormData({ ...formData, tourists: newTourists });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const activeTourists = formData.tourists.filter(t => t.name.trim() !== '');
    
    if (status !== 'idle' || activeTourists.length === 0 || !formData.email) return;

    setStatus('loading');
    try {
      const submissionData = {
        name: activeTourists[0].name, // Display name
        email: formData.email,
        arrival_date: formData.arrival_date,
        departure_date: formData.departure_date,
        purpose: formData.purpose,
        city: formData.city,
        country: formData.country,
        tourists: activeTourists,
        guests: activeTourists.length
      };

      const result = await dbService.submitRegistration(submissionData);
      if (result.success) {
        setStatus('success');
        setTimeout(() => {
          setFormData({ 
            email: '', 
            arrival_date: '', 
            departure_date: '', 
            purpose: 'Leisure', 
            city: '', 
            country: '', 
            tourists: [{ name: '', age: '', gender: 'Prefer not to say' }] 
          });
          setStatus('idle');
        }, 5000);
      } else {
        setStatus('idle');
      }
    } catch (err) {
      console.error(err);
      setStatus('idle');
    }
  };

  const titleWords = data.title.split(' ');
  const mainTitle = titleWords.length > 0 ? titleWords[0] : '';
  const italicTitle = titleWords.length > 1 ? titleWords.slice(1).join(' ') : '';

  return (
    <section id="register" className="relative pb-24 bg-white overflow-hidden scroll-mt-20">
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-20 lg:gap-32 items-start pt-12">
          
          {/* Information Column */}
          <div className="lg:w-5/12 flex flex-col justify-between py-2">
             <div className="relative z-10">
               <div className="flex items-center gap-4 text-sky-500 font-bold tracking-luxury uppercase text-[10px] mb-10">
                 <ShieldCheck className="w-5 h-5" />
                 {data.badge}
               </div>

               <h2 className="text-5xl md:text-6xl font-serif leading-[1.1] text-slate-900 mb-8 tracking-tighter">
                  <span className="font-bold">{mainTitle}</span> <span className="italic text-sky-500 block mt-2">{italicTitle}</span>
               </h2>

               <p className="text-slate-500 text-xl font-light leading-relaxed mb-16 max-w-sm">{data.description}</p>
             </div>

             <div className="relative z-10 space-y-12">
               <div className="flex items-center gap-8 group">
                 <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-sky-500 group-hover:bg-sky-500 group-hover:text-white transition-all duration-500">
                   <Ship className="w-6 h-6" strokeWidth={1.5} />
                 </div>
                 <div>
                    <p className="text-slate-400 text-[9px] font-bold uppercase tracking-luxury mb-1">Passage Gate</p>
                    <p className="text-slate-800 font-bold text-sm tracking-widest uppercase">Maritime Corridor</p>
                 </div>
               </div>
               <div className="flex items-center gap-8 group">
                 <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-sky-500 group-hover:bg-sky-500 group-hover:text-white transition-all duration-500">
                   <Compass className="w-6 h-6" strokeWidth={1.5} />
                 </div>
                 <div>
                    <p className="text-slate-400 text-[9px] font-bold uppercase tracking-luxury mb-1">Entry Point</p>
                    <p className="text-slate-800 font-bold text-sm tracking-widest uppercase">5.4116° N Entry</p>
                 </div>
               </div>
             </div>
          </div>

          {/* Form Column */}
          <div className="lg:w-7/12 py-2">
             {status === 'success' ? (
               <div className="w-full flex flex-col items-center justify-center text-center py-24 bg-slate-50 rounded-[3rem] border border-slate-100 animate-in fade-in zoom-in-95 duration-700">
                 <div className="w-20 h-20 bg-sky-500 rounded-3xl flex items-center justify-center mb-10 shadow-xl shadow-sky-500/20">
                   <CheckCircle2 className="w-10 h-10 text-white" />
                 </div>
                 <h3 className="text-3xl font-serif font-bold text-slate-900 mb-4 italic tracking-tight">Vessel Authorized</h3>
                 <p className="text-slate-500 text-lg font-light leading-relaxed mb-10">
                   Your entry has been logged in the maritime manifest. <br/>Safe passage to the volcanic soul.
                 </p>
                 <button onClick={() => setStatus('idle')} className="text-sky-500 font-bold text-[10px] uppercase tracking-luxury hover:text-slate-900 transition-all">Register Another Expedition</button>
               </div>
             ) : (
               <form className="w-full space-y-16" onSubmit={handleSubmit}>
                 {/* Metadata Fields */}
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
                   <div className="space-y-3 group md:col-span-2">
                     <p className="text-[9px] font-bold uppercase tracking-luxury text-slate-400">Email</p>
                     <div className="flex items-center gap-4 border-b border-slate-100 py-3 group-focus-within:border-sky-500 transition-all">
                       <Mail className="w-4 h-4 text-slate-300 group-focus-within:text-sky-500" />
                       <input 
                         required
                         type="email"
                         placeholder="concierge@expedition.travel"
                         value={formData.email}
                         onChange={e => setFormData({...formData, email: e.target.value})}
                         className="w-full bg-transparent text-slate-800 font-light focus:outline-none placeholder:text-slate-200"
                       />
                     </div>
                   </div>

                   <div className="space-y-3 group">
                     <p className="text-[9px] font-bold uppercase tracking-luxury text-slate-400">City of Residence</p>
                     <div className="flex items-center gap-4 border-b border-slate-100 py-3 group-focus-within:border-sky-500 transition-all">
                       <MapPin className="w-4 h-4 text-slate-300 group-focus-within:text-sky-500" />
                       <input 
                         required
                         type="text"
                         placeholder="e.g. Davao City"
                         value={formData.city}
                         onChange={e => setFormData({...formData, city: e.target.value})}
                         className="w-full bg-transparent text-slate-800 font-light focus:outline-none placeholder:text-slate-200"
                       />
                     </div>
                   </div>

                   <div className="space-y-3 group">
                     <p className="text-[9px] font-bold uppercase tracking-luxury text-slate-400">Country</p>
                     <div className="flex items-center gap-4 border-b border-slate-100 py-3 group-focus-within:border-sky-500 transition-all">
                       <Globe className="w-4 h-4 text-slate-300 group-focus-within:text-sky-500" />
                       <input 
                         required
                         type="text"
                         placeholder="e.g. Philippines"
                         value={formData.country}
                         onChange={e => setFormData({...formData, country: e.target.value})}
                         className="w-full bg-transparent text-slate-800 font-light focus:outline-none placeholder:text-slate-200"
                       />
                     </div>
                   </div>

                   <div className="space-y-3 group">
                     <p className="text-[9px] font-bold uppercase tracking-luxury text-slate-400">Arrival Cycle</p>
                     <div className="flex items-center gap-4 border-b border-slate-100 py-3 group-focus-within:border-sky-500 transition-all">
                       <Calendar className="w-4 h-4 text-slate-300 group-focus-within:text-sky-500" />
                       <input 
                         required
                         type="date"
                         value={formData.arrival_date}
                         onChange={e => setFormData({...formData, arrival_date: e.target.value})}
                         className="w-full bg-transparent text-slate-800 font-light focus:outline-none"
                       />
                     </div>
                   </div>

                   <div className="space-y-3 group">
                     <p className="text-[9px] font-bold uppercase tracking-luxury text-slate-400">Departure Cycle</p>
                     <div className="flex items-center gap-4 border-b border-slate-100 py-3 group-focus-within:border-sky-500 transition-all">
                       <Calendar className="w-4 h-4 text-slate-300 group-focus-within:text-sky-500" />
                       <input 
                         required
                         type="date"
                         value={formData.departure_date}
                         onChange={e => setFormData({...formData, departure_date: e.target.value})}
                         className="w-full bg-transparent text-slate-800 font-light focus:outline-none"
                       />
                     </div>
                   </div>
                 </div>

                 {/* Detailed Tourist Manifest */}
                 <div className="space-y-8 bg-slate-50/50 p-8 md:p-10 rounded-[2.5rem] border border-slate-100 mt-12">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                         <div className="p-2 bg-sky-500 rounded-lg shadow-lg shadow-sky-500/10">
                            <Users className="w-4 h-4 text-white" />
                         </div>
                         <p className="text-[10px] font-bold uppercase tracking-luxury text-slate-800">Tourist Manifest</p>
                      </div>
                      <button 
                        type="button" 
                        onClick={addTourist}
                        className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-sky-500 hover:text-slate-900 transition-colors"
                      >
                        <Plus className="w-4 h-4" /> Add Individual
                      </button>
                    </div>

                    <div className="space-y-12">
                       {formData.tourists.map((tourist, index) => (
                         <div key={index} className="relative grid grid-cols-1 md:grid-cols-12 gap-6 pb-10 border-b border-slate-100 last:border-0 last:pb-0 animate-in fade-in slide-in-from-bottom-2 duration-300">
                           <div className="md:col-span-1 flex items-start pt-4">
                             <span className="text-[10px] font-bold text-slate-300 bg-white w-7 h-7 rounded-lg flex items-center justify-center border border-slate-100">{index + 1}</span>
                           </div>
                           
                           <div className="md:col-span-11 grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="space-y-2 group md:col-span-2">
                                <label className="text-[8px] font-bold uppercase tracking-widest text-slate-400">Full Legal Name</label>
                                <div className="flex items-center gap-3 border-b border-slate-100 pb-2 group-focus-within:border-sky-500 transition-all">
                                  <User className="w-3.5 h-3.5 text-slate-300 group-focus-within:text-sky-500" />
                                  <input 
                                    required
                                    type="text"
                                    placeholder="Enter full name"
                                    value={tourist.name}
                                    onChange={e => handleTouristChange(index, 'name', e.target.value)}
                                    className="w-full bg-transparent text-slate-800 font-medium focus:outline-none text-sm placeholder:text-slate-200"
                                  />
                                </div>
                              </div>

                              <div className="space-y-2 group">
                                <label className="text-[8px] font-bold uppercase tracking-widest text-slate-400">Age</label>
                                <input 
                                  required
                                  type="number"
                                  placeholder="Years"
                                  value={tourist.age}
                                  onChange={e => handleTouristChange(index, 'age', e.target.value)}
                                  className="w-full bg-transparent border-b border-slate-100 pb-2 text-sm focus:outline-none focus:border-sky-500 transition-all placeholder:text-slate-200"
                                />
                              </div>

                              <div className="space-y-2 group">
                                <label className="text-[8px] font-bold uppercase tracking-widest text-slate-400">Identity/Gender</label>
                                <select 
                                  value={tourist.gender}
                                  onChange={e => handleTouristChange(index, 'gender', e.target.value)}
                                  className="w-full bg-transparent border-b border-slate-100 pb-2 text-sm focus:outline-none focus:border-sky-500 transition-all cursor-pointer"
                                >
                                  <option value="Prefer not to say">Identity</option>
                                  <option value="Male">Male</option>
                                  <option value="Female">Female</option>
                                  <option value="Non-binary">Non-binary</option>
                                </select>
                              </div>
                           </div>

                           {formData.tourists.length > 1 && (
                             <button 
                               type="button" 
                               onClick={() => removeTourist(index)}
                               className="absolute -right-2 top-0 md:top-4 p-2 text-slate-200 hover:text-red-400 transition-colors"
                             >
                               <Trash2 className="w-4 h-4" />
                             </button>
                           )}
                         </div>
                       ))}
                    </div>
                    <p className="text-[9px] font-medium text-slate-400 uppercase tracking-widest italic pt-4">
                      Security protocol requires accurate biographical data for all individuals entering the volcanic sanctuary.
                    </p>
                 </div>

                 <div className="pt-8">
                   <button 
                     disabled={status === 'loading'}
                     className="group w-full md:w-auto px-16 py-7 bg-slate-950 text-white rounded-2xl font-bold flex items-center justify-center gap-6 hover:bg-sky-500 transition-all shadow-xl active:scale-95 disabled:opacity-50"
                   >
                     {status === 'loading' ? <Loader2 className="w-6 h-6 animate-spin" /> : (
                       <>
                         <span className="text-[11px] uppercase tracking-luxury">{data.buttonLabel}</span>
                         <ArrowRight className="w-6 h-6 group-hover:translate-x-3 transition-transform" />
                       </>
                     )}
                   </button>
                 </div>
               </form>
             )}
          </div>
        </div>
      </div>
      
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-sky-50 blur-[130px] rounded-full opacity-30 -z-10"></div>
    </section>
  );
};

export default Registration;
